/**
 * A forms widget's workspace — `modules/forms/routes.ts`, the client's half (session 46,
 * `.docs/46-blog-and-forms.md` §6–§7).
 *
 * Every read and write `pages/forms/` makes, and nothing else does. The studio's half of
 * forms is the eight `forms_*` actions over `/mcp` (decision 12 — a studio inbox page is
 * *Deferred*), so there is one door here; were a studio port ever wanted, these hooks are
 * the shape it would take, one per capability, which is why they stay in one file.
 *
 * **Their own module, `forms`.** The inbox marks a lead read the moment a person opens it,
 * and a press under `usage` re-read every widget page, the balance and the sidebar for a
 * timestamp. Only a press that can **meter** — "not spam" on a lead that was never clean
 * is metered then (`forms/service.ts` `becameClean`) — names `usage` as well.
 *
 * **No `Idempotency-Key`.** None of these routes carries `idempotent()`: a form is
 * configuration, edited in place and audited, and every triage press is a state a second
 * press leaves as it is (`setSubmission` writes nothing when nothing changes).
 */

import type {
  CreateFormInput,
  FormSubmissionState,
  FormVerdict,
  UpdateFormInput,
} from '@fareast/shared'

import { useApiMutation, useApiPages, useApiQuery } from '../hooks.js'
import { queryKeys } from '../keys.js'
import type { DetailOptions } from './requests.js'

/** What the inbox narrows by — `FormSubmissionsQuerySchema`, as the page asks it. */
export type SubmissionsFilter = {
  readonly form?: string | undefined
  readonly verdict?: FormVerdict | undefined
  readonly state?: FormSubmissionState | undefined
  readonly q?: string | undefined
}

/** `GET …/widgets/:ref/forms` — every form on the widget, with its public address. */
export function useForms(ref: string, options: DetailOptions = {}) {
  return useApiQuery({
    key: queryKeys.forms.list(ref),
    request: (api) => api.rest.clients.me.widgets[':ref'].forms.$get({ param: { ref } }),
    ...detail(options),
  })
}

/** `GET …/widgets/:ref/forms/:key` — one form, as its editor opens it. */
export function useForm(ref: string, key: string, options: DetailOptions = {}) {
  return useApiQuery({
    key: queryKeys.forms.form(ref, key),
    request: (api) =>
      api.rest.clients.me.widgets[':ref'].forms[':key'].$get({ param: { ref, key } }),
    ...detail(options),
  })
}

/**
 * `GET …/widgets/:ref/submissions` — the inbox, newest first, a page at a time.
 *
 * The query is built in one fixed order — form, verdict, state, q — and an absent field
 * is left out rather than sent empty, so one question is always one address (and one
 * recording, `pnpm world:record`). **No `limit`**: the route's default is what a page
 * is, for `useWorkInFlight`'s reason, and the cursor is the only thing that travels.
 */
export function useSubmissions(ref: string, filter: SubmissionsFilter) {
  return useApiPages({
    key: queryKeys.forms.submissions(ref, filter),
    request: (api, cursor) =>
      api.rest.clients.me.widgets[':ref'].submissions.$get({
        param: { ref },
        query: {
          ...(filter.form === undefined ? {} : { form: filter.form }),
          ...(filter.verdict === undefined ? {} : { verdict: filter.verdict }),
          ...(filter.state === undefined ? {} : { state: filter.state }),
          ...(filter.q === undefined ? {} : { q: filter.q }),
          ...(cursor === undefined ? {} : { cursor }),
        },
      }),
    nextCursor: (page) => page.next_cursor,
  })
}

/** `GET …/widgets/:ref/submissions/:receipt` — one lead, with its delivery log. */
export function useSubmission(ref: string, receipt: string, options: DetailOptions = {}) {
  return useApiQuery({
    key: queryKeys.forms.submission(ref, receipt),
    request: (api) =>
      api.rest.clients.me.widgets[':ref'].submissions[':receipt'].$get({
        param: { ref, receipt },
      }),
    ...detail(options),
  })
}

/**
 * The CSV export of one form's submissions — `GET …/submissions.csv?form=`.
 *
 * An address and not a read, for `fileDownloadHref`'s reason: the server streams it a
 * batch at a time, and the browser is the thing that should hold a download, not a
 * `fetch` that buffers it to find out whether it worked (`files/DownloadLink.tsx`).
 * One form, because its declared fields are the columns.
 */
export function submissionsCsvHref(ref: string, form: string): string {
  const query = new URLSearchParams({ form })
  return `/api/clients/me/widgets/${encodeURIComponent(ref)}/submissions.csv?${query.toString()}`
}

/* ---------------------------------------------------------------------- writes */

/** `POST …/widgets/:ref/forms` — a new form. `site:publish`. */
export function useCreateForm() {
  return useApiMutation({
    module: 'forms',
    request: (api, variables: { readonly ref: string; readonly input: CreateFormInput }) =>
      api.rest.clients.me.widgets[':ref'].forms.$post({
        param: { ref: variables.ref },
        json: variables.input,
      }),
  })
}

/**
 * `PATCH …/widgets/:ref/forms/:key` — the fields that changed, and only those. What is
 * left out is left alone, which is what keeps the write-only Turnstile secret where it
 * is when nobody typed a new one.
 */
export function useUpdateForm() {
  return useApiMutation({
    module: 'forms',
    request: (
      api,
      variables: { readonly ref: string; readonly key: string; readonly changes: UpdateFormInput },
    ) =>
      api.rest.clients.me.widgets[':ref'].forms[':key'].$patch({
        param: { ref: variables.ref, key: variables.key },
        json: variables.changes,
      }),
  })
}

/**
 * `POST …/forms/:key/close` and `…/reopen`. A closed form's door answers `410`; every
 * lead it already took is kept.
 */
export function useSetFormClosed() {
  return useApiMutation({
    module: 'forms',
    request: (
      api,
      variables: { readonly ref: string; readonly key: string; readonly closed: boolean },
    ) => {
      const param = { ref: variables.ref, key: variables.key }
      return variables.closed
        ? api.rest.clients.me.widgets[':ref'].forms[':key'].close.$post({ param })
        : api.rest.clients.me.widgets[':ref'].forms[':key'].reopen.$post({ param })
    },
  })
}

/**
 * The triage presses a person makes on a lead — `POST …/submissions/:receipt/<press>`.
 * Each answers the lead as it now is, with its delivery log.
 */
export type TriagePress = 'handled' | 'open' | 'spam' | 'not-spam'

/**
 * Mark handled, reopen, spam, not spam.
 *
 * `alsoInvalidates: ['usage']`, because "not spam" on a lead that was never clean meters
 * it then — the widget page's count moves — and a press list that knew which of the four
 * could would be this file deciding something the service decides.
 */
export function useTriageSubmission() {
  return useApiMutation({
    module: 'forms',
    request: (
      api,
      variables: { readonly ref: string; readonly receipt: string; readonly press: TriagePress },
    ) =>
      api.rest.clients.me.widgets[':ref'].submissions[':receipt'][
        ':press{read|handled|open|spam|not-spam}'
      ].$post({
        param: { ref: variables.ref, receipt: variables.receipt, press: variables.press },
      }),
    alsoInvalidates: ['usage'],
  })
}

/**
 * `POST …/submissions/:receipt/read` — the lead was opened by a person. Its own hook
 * because it is the one press nobody makes: the page sends it once, on opening a lead
 * that has not been read, and it can move nothing but the inbox's emphasis.
 */
export function useMarkSubmissionRead() {
  return useApiMutation({
    module: 'forms',
    request: (api, variables: { readonly ref: string; readonly receipt: string }) =>
      api.rest.clients.me.widgets[':ref'].submissions[':receipt'][
        ':press{read|handled|open|spam|not-spam}'
      ].$post({ param: { ref: variables.ref, receipt: variables.receipt, press: 'read' } }),
  })
}

/**
 * `POST …/submissions/:receipt/resend` — forward a clean lead again, in a new round:
 * really sent again, to the form's recipients as they are now. Queued, so `202`.
 */
export function useResendSubmission() {
  return useApiMutation({
    module: 'forms',
    request: (api, variables: { readonly ref: string; readonly receipt: string }) =>
      api.rest.clients.me.widgets[':ref'].submissions[':receipt'].resend.$post({
        param: { ref: variables.ref, receipt: variables.receipt },
      }),
  })
}

function detail(options: DetailOptions) {
  return {
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
    ...(options.inline === undefined ? {} : { inline: options.inline }),
  }
}
